import { apiMerchant } from "@/api";

import {
  normalizeStorefrontAppearance,
  type StorefrontAppearance,
} from "./appearance";

/**
 * WHERE THE STOREFRONT READS APPEARANCE
 *
 * `Source`, not `Repository`: the storefront only READS. The portal writes.
 * Giving this port a `save` would let the customer storefront reconfigure the
 * merchant's store. Until a real endpoint exists, defaults reproduce the prior
 * look (no pixel change). The port exists now so the rest codes against the
 * CONTRACT, not the constant.
 */
export interface StorefrontAppearanceSource {
  /**
   * Never rejects. Appearance is decoration: a store without matching colors
   * still sells; one that fails to open because color did not load does not.
   * Network/404/corrupt payload all become the default. Always `async` so HTTP
   * drops in without call-site changes. `merchantId` is a PARAMETER (per store).
   */
  get(merchantId: string): Promise<StorefrontAppearance>;
}

/**
 * Always the default — safest read. Goes through
 * {@link normalizeStorefrontAppearance} for a fresh object each call and a
 * single exit path.
 */
export class DefaultStorefrontAppearanceSource
  implements StorefrontAppearanceSource
{
  async get(_merchantId: string): Promise<StorefrontAppearance> {
    return normalizeStorefrontAppearance(undefined);
  }
}

/**
 * Storage KEY the portal writes, mirrored here. Must match
 * `merchant-portal-web/.../localStorageKeys.ts` or the storefront silently
 * falls back to defaults.
 */
export function storefrontAppearanceStorageKey(merchantId: string): string {
  return `storefront_appearance_v1:${merchantId}`;
}

/**
 * Portal storage envelope: payload in `data`, with `version` and `updatedAt`
 * outside so later format migrations need not inspect contents.
 */
type StoredEnvelope = {
  version: number;
  updatedAt: string;
  data: unknown;
};

/**
 * Reads appearance from `localStorage` on THIS origin via
 * {@link storefrontAppearanceStorageKey}. In local Vite same-origin dev this is
 * real sync. Partition is by origin (scheme+host+PORT) — diverging ports or
 * published domains need the HTTP endpoint. Flag-gated; off by default.
 */
export class LocalStorageStorefrontAppearanceSource
  implements StorefrontAppearanceSource
{
  async get(merchantId: string): Promise<StorefrontAppearance> {
    const raw = safeRead(storefrontAppearanceStorageKey(merchantId));
    if (!raw) return normalizeStorefrontAppearance(undefined);

    try {
      const parsed = JSON.parse(raw) as Partial<StoredEnvelope>;
      return normalizeStorefrontAppearance(parsed?.data ?? parsed);
    } catch {
      return normalizeStorefrontAppearance(undefined);
    }
  }
}

function safeRead(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

export const STOREFRONT_APPEARANCE_SOURCE_MODES = [
  "default",
  "local",
  "http",
] as const;

export type StorefrontAppearanceSourceMode =
  (typeof STOREFRONT_APPEARANCE_SOURCE_MODES)[number];

/**
 * Runtime key that enables `localStorage` reads. Exists beyond `VITE_*` (build-
 * time) so homolog can flip without republishing — set in devtools and reload.
 */
export const STOREFRONT_APPEARANCE_SOURCE_KEY =
  "meucontroleai:storefront-appearance-source";

function isSourceMode(value: unknown): value is StorefrontAppearanceSourceMode {
  return STOREFRONT_APPEARANCE_SOURCE_MODES.includes(
    value as StorefrontAppearanceSourceMode,
  );
}

/**
 * Which source to use: runtime `localStorage` override wins over env default.
 */
export function resolveStorefrontAppearanceSourceMode(
  envDefault: string | undefined,
): StorefrontAppearanceSourceMode {
  const override = safeRead(STOREFRONT_APPEARANCE_SOURCE_KEY)?.trim();
  if (isSourceMode(override)) return override;
  if (isSourceMode(envDefault?.trim())) return envDefault!.trim() as StorefrontAppearanceSourceMode;
  return "http";
}

/**
 * Picks the source on EVERY read (not once at module load) so flipping the flag
 * and reloading is enough.
 */
export class FlaggedStorefrontAppearanceSource
  implements StorefrontAppearanceSource
{
  constructor(
    private readonly sources: Record<
      StorefrontAppearanceSourceMode,
      StorefrontAppearanceSource
    >,
    private readonly envDefault: string | undefined,
  ) {}

  async get(merchantId: string): Promise<StorefrontAppearance> {
    const mode = resolveStorefrontAppearanceSourceMode(this.envDefault);
    return this.sources[mode].get(merchantId);
  }
}

/**
 * Public GET /merchants/:id/storefront-appearance. Never rejects — network/404/
 * bad payload become the default. `apiMerchant` already ends in `/merchants`.
 */
export class HttpStorefrontAppearanceSource
  implements StorefrontAppearanceSource
{
  async get(merchantId: string): Promise<StorefrontAppearance> {
    try {
      const { data } = await apiMerchant.get<unknown>(
        `/${encodeURIComponent(merchantId)}/storefront-appearance`,
      );
      return normalizeStorefrontAppearance(data);
    } catch {
      return normalizeStorefrontAppearance(undefined);
    }
  }
}

/**
 * Sole composition point. No flag → HTTP. `local` for same-origin dev;
 * `default` forces the factory palette.
 */
export const storefrontAppearanceSource: StorefrontAppearanceSource =
  new FlaggedStorefrontAppearanceSource(
    {
      default: new DefaultStorefrontAppearanceSource(),
      local: new LocalStorageStorefrontAppearanceSource(),
      http: new HttpStorefrontAppearanceSource(),
    },
    import.meta.env.VITE_STOREFRONT_APPEARANCE_SOURCE,
  );
